import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "etcd-snapshot-config" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = {
  key: 0,
  class: "row"
}
const _hoisted_5 = { class: "col span-6" }
const _hoisted_6 = { class: "col span-6" }

import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import { LabeledInput } from '@components/Form/LabeledInput';
import { RadioGroup } from '@components/Form/Radio';
import UnitInput from '@shell/components/form/UnitInput.vue';
import S3Config from '@shell/edit/provisioning.cattle.io.cluster/tabs/etcd/S3Config.vue';
import isEmpty from 'lodash/isEmpty';

export interface EtcdSnapshotS3 {
  bucket?: string;
  cloudCredentialName?: string | null;
  endpoint?: string;
  endpointCA?: string;
  folder?: string;
  region?: string;
  retention?: number | null;
  skipSSLVerify?: boolean;
}

export interface EtcdSnapshotConfigValue {
  disableSnapshots?: boolean;
  snapshotScheduleCron?: string;
  snapshotRetention?: number | null;
  s3?: EtcdSnapshotS3 | null;
  [key: string]: unknown;
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'EtcdSnapshotConfig' },
  __name: 'EtcdSnapshotConfig',
  props: {
    mode: {},
    value: { default: null },
    s3Backup: { type: Boolean, default: false },
    namespace: {},
    registerBeforeHook: {}
  },
  emits: ["update:value", "s3-backup-changed", "validation-changed"],
  setup(__props: any, { emit: __emit }) {

/**
 * etcd snapshot (backup) configuration form.
 *
 * Shared between RKE2/K3s clusters provisioned by Rancher (`spec.rkeConfig.etcd` on provisioning.cattle.io.cluster)
 * and imported RKE2/K3s clusters (`rke2Config.etcd` / `k3sConfig.etcd` on the management/norman cluster), which use the same
 * object shape: `{ disableSnapshots, snapshotScheduleCron, snapshotRetention, s3 }`.
 *
 * The component never mutates `value`, it emits a new etcd object via `update:value` on every change.
 */


const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const etcd = computed<EtcdSnapshotConfigValue>(() => props.value || {});
const snapshotsEnabled = computed(() => etcd.value.disableSnapshots !== true);

const disableSnapshotsOptions = computed(() => [
  { value: true, label: t('generic.disable') },
  { value: false, label: t('generic.enable') },
]);
const s3BackupOptions = computed(() => [
  { value: false, label: t('generic.disable') },
  { value: true, label: t('generic.enable') },
]);

function update(changes: Partial<EtcdSnapshotConfigValue>) {
  emit('update:value', { ...etcd.value, ...changes });
}

function s3BackupChanged(enabled: boolean) {
  // Keep any existing s3 config when (re)enabling, otherwise editing a cluster with s3 defined would clear it
  update({ s3: enabled ? (isEmpty(etcd.value.s3) ? {} : etcd.value.s3) : null });
  emit('s3-backup-changed', enabled);
  emit('validation-changed', !enabled);
}

function disableSnapshotsChanged(disableSnapshots: boolean) {
  if (disableSnapshots) {
    update({ disableSnapshots, s3: null });
    emit('s3-backup-changed', false);
    emit('validation-changed', true);
  } else {
    update({ disableSnapshots });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(RadioGroup), {
          value: !snapshotsEnabled.value,
          name: "etcd-disable-snapshots",
          "data-testid": "etcd-disable-snapshots",
          options: disableSnapshotsOptions.value,
          label: _unref(t)('cluster.rke2.etcd.disableSnapshots.label'),
          mode: __props.mode,
          "onUpdate:value": disableSnapshotsChanged
        }, null, 8, ["value", "options", "label", "mode"])
      ])
    ]),
    _cache[8] || (_cache[8] = _createTextVNode()),
    (snapshotsEnabled.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(LabeledInput), {
              value: etcd.value.snapshotScheduleCron,
              type: "cron",
              placeholder: "0 * * * *",
              "data-testid": "etcd-snapshot-schedule-cron",
              mode: __props.mode,
              label: _unref(t)('cluster.rke2.etcd.snapshotScheduleCron.label'),
              "onUpdate:value": _cache[0] || (_cache[0] = (snapshotScheduleCron) => update({ snapshotScheduleCron }))
            }, null, 8, ["value", "mode", "label"])
          ]),
          _cache[4] || (_cache[4] = _createTextVNode()),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(UnitInput, {
              value: etcd.value.snapshotRetention ?? undefined,
              "data-testid": "etcd-snapshot-retention",
              mode: __props.mode,
              label: _unref(t)('cluster.rke2.etcd.snapshotRetention.label'),
              suffix: __props.s3Backup ? _unref(t)('cluster.rke2.snapshots.s3Suffix') : _unref(t)('cluster.rke2.snapshots.suffix'),
              tooltip: __props.s3Backup ? _unref(t)('cluster.rke2.etcd.snapshotRetention.tooltip') : undefined,
              "onUpdate:value": _cache[1] || (_cache[1] = (snapshotRetention) => update({ snapshotRetention }))
            }, null, 8, ["value", "mode", "label", "suffix", "tooltip"])
          ])
        ]))
      : _createCommentVNode("", true),
    _cache[9] || (_cache[9] = _createTextVNode()),
    (snapshotsEnabled.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _cache[5] || (_cache[5] = _createElementVNode("div", { class: "spacer" }, null, -1)),
          _cache[6] || (_cache[6] = _createTextVNode()),
          _createVNode(_unref(RadioGroup), {
            value: __props.s3Backup,
            name: "etcd-s3",
            "data-testid": "etcd-s3-backup",
            options: s3BackupOptions.value,
            label: _unref(t)('cluster.rke2.etcd.s3backups.label'),
            mode: __props.mode,
            "onUpdate:value": s3BackupChanged
          }, null, 8, ["value", "options", "label", "mode"]),
          _cache[7] || (_cache[7] = _createTextVNode()),
          (__props.s3Backup)
            ? (_openBlock(), _createBlock(S3Config, {
                key: 0,
                value: etcd.value.s3 || undefined,
                "local-retention-count": etcd.value.snapshotRetention ?? undefined,
                namespace: __props.namespace,
                "register-before-hook": __props.registerBeforeHook,
                mode: __props.mode,
                "onUpdate:value": _cache[2] || (_cache[2] = (s3) => update({ s3 })),
                onValidationChanged: _cache[3] || (_cache[3] = (valid) => emit('validation-changed', valid))
              }, null, 8, ["value", "local-retention-count", "namespace", "register-before-hook", "mode"]))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})